import clsx from 'clsx';
import type { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/* ---------------------------------------------------------------- Card */

export function Card({
  className,
  variant,
  padding = 'md',
  ...rest
}: HTMLAttributes<HTMLDivElement> & {
  /** flat = no shadow (nested cards); inverse = night tile (hero, next tee time) */
  variant?: 'flat' | 'inverse';
  padding?: 'none' | 'sm' | 'md' | 'lg';
}) {
  return (
    <div
      className={clsx(
        'ui-card',
        variant && `ui-card--${variant}`,
        padding !== 'md' && `ui-card--pad-${padding}`,
        className,
      )}
      {...rest}
    />
  );
}

/** Card title row: title (+ optional subtitle) left, actions right. */
export function CardHeader({
  title,
  subtitle,
  actions,
  as: Tag = 'h2',
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  as?: 'h2' | 'h3' | 'h4';
}) {
  return (
    <div className="ui-card__header">
      <div className="ui-card__titles">
        <Tag className="ui-card__title">{title}</Tag>
        {subtitle ? <p className="ui-card__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="ui-card__actions">{actions}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------- Badge / StatusChip */

export type Tone = 'default' | 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export function Badge({
  tone = 'default',
  className,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & { tone?: Tone | 'live' | 'accent' }) {
  return (
    <span
      className={clsx(
        'ui-badge',
        tone !== 'default' && tone !== 'neutral' && `ui-badge--${tone}`,
        className,
      )}
      {...rest}
    />
  );
}

/**
 * Status pill: soft background + dot (or icon) + text — colour is never the only signal.
 * `live` = lime LIVE pill with a pulsing dot.
 */
export function StatusChip({
  tone = 'neutral',
  icon,
  dot = !icon,
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & {
  tone?: Tone | 'live';
  icon?: ReactNode;
  dot?: boolean;
}) {
  return (
    <span className={clsx('ui-chip', `ui-chip--${tone}`, className)} {...rest}>
      {icon ? (
        <span className="ui-chip__icon" aria-hidden="true">
          {icon}
        </span>
      ) : dot ? (
        <i className="ui-chip__dot" aria-hidden="true" />
      ) : null}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------ Spinner/Empty */

export function Spinner({ label, size = 24 }: { label?: string; size?: number }) {
  return (
    <div
      className="ui-spinner"
      role="status"
      aria-label={label ?? 'loading'}
      style={size !== 24 ? { width: size, height: size } : undefined}
    />
  );
}

/** Empty state. `children` alone keeps the old one-line form; icon/title/action add structure. */
export function Empty({
  children,
  icon,
  title,
  action,
  className,
}: {
  children?: ReactNode;
  icon?: ReactNode;
  title?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('ui-empty', className)}>
      {icon ? (
        <div className="ui-empty__icon" aria-hidden="true">
          {icon}
        </div>
      ) : null}
      {title ? <p className="ui-empty__title">{title}</p> : null}
      {children ? <div className="ui-empty__body">{children}</div> : null}
      {action ? <div className="ui-empty__action">{action}</div> : null}
    </div>
  );
}

/* -------------------------------------------------------------- KPI card */

export function KpiCard({
  label,
  value,
  unit,
  sub,
  tone = 'default',
  progress,
  icon,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  sub?: ReactNode;
  /** coloured top rule + value (warning: pending sign-offs, danger: anomalies) */
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'info';
  /** 0..1 progress bar under the value */
  progress?: number;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('ui-kpi', tone !== 'default' && `ui-kpi--${tone}`, className)}>
      <div className="ui-kpi__label">
        {icon ? (
          <span className="ui-kpi__icon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        {label}
      </div>
      <div className="ui-kpi__value">
        <b>{value}</b>
        {unit ? <small>{unit}</small> : null}
      </div>
      {progress !== undefined ? (
        <div className="ui-kpi__bar" aria-hidden="true">
          <span style={{ width: `${Math.max(0, Math.min(1, progress)) * 100}%` }} />
        </div>
      ) : null}
      {sub ? <div className="ui-kpi__sub">{sub}</div> : null}
    </div>
  );
}

/* --------------------------------------------------------------- Avatar */

const CJK = /[\p{Script=Hangul}\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u;

/**
 * Monogram for an avatar: letters only, one script. The name is cut before a subtitle
 * ('KATPGA — Korea Asia Tour PGA', 'Club · 동호회', 'Course (Tan Son Nhat)'), punctuation is
 * dropped, then Hangul / CJK names give their first character and Latin names the initials of
 * their first two words (one word → its first two letters). Words of another script are skipped.
 */
const initialsOf = (name: string): string => {
  const head = name.split(/\s[—–·|/-]\s|[(（[·—]/u)[0] ?? '';
  const words = (head.trim() ? head : name)
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  const first = words[0];
  if (!first) return '?';
  if (CJK.test(first[0]!)) return first[0]!;
  const latin = words.filter((w) => !CJK.test(w[0]!));
  const letters =
    latin.length > 1 ? latin[0]![0]! + latin[1]![0]! : Array.from(latin[0]!).slice(0, 2).join('');
  return letters.toLocaleUpperCase();
};

export function Avatar({
  name,
  src,
  size = 32,
  tone = 'accent',
  className,
}: {
  name: string;
  src?: string | null;
  size?: number;
  tone?: 'accent' | 'night' | 'neutral' | 'sun';
  className?: string;
}) {
  return (
    <span
      className={clsx('ui-avatar', `ui-avatar--${tone}`, className)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
      title={name}
      aria-hidden="true"
    >
      {src ? <img src={src} alt="" width={size} height={size} /> : initialsOf(name)}
    </span>
  );
}

/* ------------------------------------------------------------ Flag chip */

const FLAGS: Record<string, ReactNode> = {
  VN: (
    <>
      <rect width="30" height="20" fill="#DA251D" />
      <polygon
        fill="#FFFF00"
        points="15,4.2 16.4,8.5 20.9,8.5 17.3,11.1 18.6,15.4 15,12.8 11.4,15.4 12.7,11.1 9.1,8.5 13.6,8.5"
      />
    </>
  ),
  KR: (
    <>
      <rect width="30" height="20" fill="#FFF" />
      <g transform="translate(15 10) rotate(33.7)">
        <path d="M-5 0 A5 5 0 0 1 5 0 Z" fill="#CD2E3A" />
        <path d="M-5 0 A5 5 0 0 0 5 0 Z" fill="#0047A0" />
        <circle cx="-2.5" cy="0" r="2.5" fill="#CD2E3A" />
        <circle cx="2.5" cy="0" r="2.5" fill="#0047A0" />
      </g>
      <g fill="#000">
        <rect x="3.6" y="3.2" width="4" height="0.9" transform="rotate(-56 5.6 3.6)" />
        <rect x="22.4" y="3.2" width="4" height="0.9" transform="rotate(56 24.4 3.6)" />
        <rect x="3.6" y="15.9" width="4" height="0.9" transform="rotate(56 5.6 16.3)" />
        <rect x="22.4" y="15.9" width="4" height="0.9" transform="rotate(-56 24.4 16.3)" />
      </g>
    </>
  ),
  JP: (
    <>
      <rect width="30" height="20" fill="#FFF" />
      <circle cx="15" cy="10" r="6" fill="#BC002D" />
    </>
  ),
  CN: (
    <>
      <rect width="30" height="20" fill="#EE1C25" />
      <polygon
        fill="#FFFF00"
        points="6,2.5 6.9,5.2 9.8,5.2 7.5,6.9 8.4,9.6 6,7.9 3.6,9.6 4.5,6.9 2.2,5.2 5.1,5.2"
      />
    </>
  ),
  TH: (
    <>
      <rect width="30" height="20" fill="#A51931" />
      <rect y="3.33" width="30" height="13.33" fill="#F4F5F8" />
      <rect y="6.67" width="30" height="6.67" fill="#2D2A4A" />
    </>
  ),
};

/**
 * Nationality chip: drawn flag for the common field nationalities (no emoji: they render as
 * letters on Windows), always with the ISO code as text.
 */
export function FlagChip({
  code,
  showCode = true,
  className,
}: {
  /** ISO 3166-1 alpha-2 (case-insensitive) */
  code: string;
  showCode?: boolean;
  className?: string;
}) {
  const cc = code.toUpperCase();
  const flag = FLAGS[cc];
  return (
    <span className={clsx('ui-flag', className)}>
      {flag ? (
        <svg className="ui-flag__img" viewBox="0 0 30 20" aria-hidden="true" focusable="false">
          {flag}
          <rect width="30" height="20" fill="none" stroke="rgba(0,0,0,.18)" strokeWidth="1" />
        </svg>
      ) : null}
      {showCode || !flag ? <span className="ui-flag__code">{cc}</span> : null}
    </span>
  );
}

/* ------------------------------------------------------ Score pill / To par */

export type ScoreKind = 'eagle' | 'birdie' | 'par' | 'bogey' | 'double' | 'empty';

/** strokes vs par → score shape (eagle or better / birdie / par / bogey / double or worse) */
export function scoreKind(strokes: number | null | undefined, par: number | null | undefined) {
  if (strokes == null || par == null) return 'empty' as const;
  const d = strokes - par;
  if (d <= -2) return 'eagle' as const;
  if (d === -1) return 'birdie' as const;
  if (d === 0) return 'par' as const;
  if (d === 1) return 'bogey' as const;
  return 'double' as const;
}

/**
 * Hole score with the championship convention — colour AND shape: ○ birdie, ◎ filled eagle,
 * □ bogey, ■ filled double+. The kind is announced to screen readers.
 */
export function ScorePill({
  strokes,
  par,
  pickedUp,
  size = 'md',
  className,
}: {
  strokes: number | null | undefined;
  par: number | null | undefined;
  pickedUp?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const { t } = useTranslation('common');
  const kind = pickedUp ? 'double' : scoreKind(strokes, par);
  const text = pickedUp ? 'X' : strokes == null ? '–' : String(strokes);
  return (
    <span
      className={clsx(
        'ui-score',
        `ui-score--${kind}`,
        size !== 'md' && `ui-score--${size}`,
        className,
      )}
      data-score={kind}
    >
      <span aria-hidden="true">{text}</span>
      <span className="visually-hidden">
        {strokes == null && !pickedUp
          ? '–'
          : `${text} ${kind !== 'empty' ? t(`ui.score.${kind}`) : ''}`}
      </span>
    </span>
  );
}

/** −7 / E / +2 in broadcast numerals; red under, slate over. */
export function ToPar({
  value,
  className,
  size = 'md',
}: {
  value: number | null | undefined;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const text = value == null ? '–' : value === 0 ? 'E' : value > 0 ? `+${value}` : `−${-value}`;
  return (
    <span
      className={clsx(
        'ui-topar',
        value != null && value < 0 && 'ui-topar--under',
        value != null && value > 0 && 'ui-topar--over',
        size !== 'md' && `ui-topar--${size}`,
        className,
      )}
    >
      {text}
    </span>
  );
}
